import { useCallback, useMemo } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import Caption from 'LuxKit/Typography/Caption'
import LineHistoryIcon from 'LuxKit/Icons/line/LineHistoryIcon'
import { tripTypeToFareTypeMapper } from 'api/flights'
import { bumpRecentFlightSearch, deriveFareType, type StandaloneFlightSearchParam } from 'lib/flights/flightUtils'
import { FlightsClassTypes, FlightsFareTypes, fareClassToUrlMap } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import qs from 'qs'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Group from 'components/utils/Group'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'

type FlightQueryParams = StandaloneFlightSearchParam & { searchId: string }

const RecentSearchContainer = styled(Clickable)`
  display: flex;
  padding: ${rem(8)};
  flex-direction: column;
  gap: ${rem(8)};
  justify-content: flex-start;
  align-items: flex-start;
  transition: background-color 0.2s;
  min-width: 100%;

  ${mediaQueryUp.tablet} {
    min-width: ${rem(350)};
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`

const IconBlock = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const SubHeader = styled(Caption)`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const InlineBodyText = styled(BodyText)`
  display: inline-block;
  text-align: center;
  max-width: ${rem(100)};
`

function getStandaloneFlightSearchUrlFromRecentSearch(recentSearch: App.RecentFlightSearch) {
  const mappedFareType = tripTypeToFareTypeMapper[recentSearch.tripType]
  const hasSecondLeg = recentSearch.itinerary.length > 1
  const fareType = deriveFareType(hasSecondLeg, mappedFareType)

  const firstLeg = recentSearch.itinerary[0]
  const secondLeg = recentSearch.itinerary[1]

  const departDate = moment(firstLeg.departureDate, 'DD/MM/YYYY').format(ISO_DATE_FORMAT)
  const returnDate = fareType === FlightsFareTypes.RETURN && secondLeg?.departureDate
    ? moment(secondLeg.departureDate, 'DD/MM/YYYY').format(ISO_DATE_FORMAT)
    : null
  const internalFareClass = recentSearch.fareClass ?? FlightsClassTypes.ECONOMY
  const queryParams: FlightQueryParams = {
    originAirportCode: firstLeg.departureAirport,
    originAirportName: firstLeg.departureAirportName,
    destinationAirportCode: firstLeg.arrivalAirport,
    destinationAirportName: firstLeg.arrivalAirportName,
    departDate,
    returnDate,
    adults: recentSearch.passengers.numAdults,
    children: recentSearch.passengers.numChildren,
    infants: recentSearch.passengers.numInfants,
    fareClass: fareClassToUrlMap[internalFareClass as FlightsClassTypes] ?? fareClassToUrlMap[FlightsClassTypes.ECONOMY],
    fareType,
    searchId: recentSearch.searchId,
  }

  if (fareType === FlightsFareTypes.MULTI_CITY) {
    const flights = recentSearch.itinerary.map(itinerary => ({
      originAirportCode: itinerary.departureAirport,
      originAirportName: itinerary.departureAirportName,
      destinationAirportCode: itinerary.arrivalAirport,
      destinationAirportName: itinerary.arrivalAirportName,
      departDate: moment(itinerary.departureDate, 'DD/MM/YYYY').format(ISO_DATE_FORMAT),
    }))
    return `/flights-search-results-multi-city?${qs.stringify({ ...queryParams, flights }, { skipNulls: true })}`
  }

  return `/flights-search-results?${qs.stringify(queryParams, { skipNulls: true })}`
}

function getSubHeader(intl: IntlShape, recentSearch: App.RecentFlightSearch, fareTypeLabel: string | undefined, travellerText: string, fareCabinLabelMap: Record<FlightsClassTypes, string>) {
  const fareClassLabel = recentSearch.fareClass && fareCabinLabelMap[recentSearch.fareClass as FlightsClassTypes]

  const datesLabel = recentSearch.itinerary.map((itinerary, index) => {
    const date = moment(itinerary.departureDate, 'DD/MM/YYYY').toDate()
    const isLast = index === recentSearch.itinerary.length - 1
    return isLast
      ? intl.formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
      : intl.formatDate(date, { day: 'numeric', month: 'short' })
  }).join(' - ')

  const elements = [
    fareClassLabel,
    datesLabel,
    travellerText,
    fareTypeLabel,
  ]

  const separatorChar = intl.formatMessage(middleDotSeparatorMessage)
  return elements.filter(Boolean).join(` ${separatorChar} `)
}

export interface RecentFlightSearchProps {
  recentSearch: App.RecentFlightSearch
}

function getMultiCityItineraryLabel(intl: IntlShape, itinerary: Array<App.RecentFlightSearchItineraryLeg>) {
  const cities = Array.from(new Set(itinerary.map(({ departureCityDesc, arrivalCityDesc }) => [departureCityDesc, arrivalCityDesc]).flat()))

  return intl.formatList(cities, { type: 'unit' })
}

function RecentFlightSearch({ recentSearch }: RecentFlightSearchProps) {
  const intl = useIntl()
  const { fareTypeLabelMap, fareCabinLabelMap } = useFlightFareLabels()
  const searchUrl = getStandaloneFlightSearchUrlFromRecentSearch(recentSearch)
  const mappedFareType = useMemo(() => tripTypeToFareTypeMapper[recentSearch.tripType], [recentSearch.tripType])
  const hasSecondLegWithDate = useMemo(() => !!recentSearch.itinerary[1]?.departureDate, [recentSearch.itinerary])
  const fareType = useMemo(() => deriveFareType(hasSecondLegWithDate, mappedFareType), [hasSecondLegWithDate, mappedFareType])
  const onClickRecent = useCallback(() => bumpRecentFlightSearch(recentSearch), [recentSearch])
  const fareTypeLabel = fareTypeLabelMap[fareType]

  const totalTravellers = recentSearch.passengers.numAdults + recentSearch.passengers.numChildren + recentSearch.passengers.numInfants
  const travellerText = intl.formatMessage(
    {
      id: 'FygB_19vTr9dSsYjR1c3',
      defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
      description: 'Traveller count displayed in recent flight search summary',
    },
    { count: totalTravellers },
  )

  const departureAirportCodeLabel = intl.formatMessage(
    {
      id: 'giFJeWRbdKyk6BBQ2wnq',
      defaultMessage: '({airportCode})',
      description: 'Departure airport code displayed in parentheses in recent flight search',
    },
    { airportCode: recentSearch.itinerary[0].departureAirport },
  )

  const arrivalAirportCodeLabel = intl.formatMessage(
    {
      id: 'WNZ6vaZ6o2xgaNtFuapD',
      defaultMessage: '({airportCode})',
      description: 'Arrival airport code displayed in parentheses in recent flight search',
    },
    { airportCode: recentSearch.itinerary[0].arrivalAirport },
  )

  return (
    <RecentSearchContainer to={searchUrl} onClick={onClickRecent}>
      <Group direction="horizontal" gap={12}>
        <IconBlock>
          <LineHistoryIcon size="M" />
        </IconBlock>
        <Group direction="vertical" gap={4}>
          {fareType === FlightsFareTypes.MULTI_CITY && <BodyText variant="medium" colour="normal-contrast" wrap="pretty" weight="bold">{getMultiCityItineraryLabel(intl, recentSearch.itinerary)}</BodyText>}
          {fareType !== FlightsFareTypes.MULTI_CITY && <Group direction="horizontal" gap={4} horizontalAlign="space-between">
            <InlineBodyText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate" align="center">{recentSearch.itinerary[0].departureCityDesc || recentSearch.itinerary[0].departureAirportName}</InlineBodyText>
            <InlineBodyText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate" align="center">{departureAirportCodeLabel}</InlineBodyText>
            {fareType === FlightsFareTypes.ONE_WAY && <LineArrowRightIcon size="S" />}
            {fareType === FlightsFareTypes.RETURN && <LineArrowsSwapHorizontalIcon size="S" />}
            <InlineBodyText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate" align="center">{recentSearch.itinerary[0].arrivalCityDesc || recentSearch.itinerary[0].arrivalAirportName}</InlineBodyText>
            <InlineBodyText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate" align="center">{arrivalAirportCodeLabel}</InlineBodyText>
          </Group>}
          <SubHeader wrap="pretty" variant="medium">{getSubHeader(intl, recentSearch, fareTypeLabel, travellerText, fareCabinLabelMap)}</SubHeader>
        </Group>
      </Group>
    </RecentSearchContainer>
  )
}

export default RecentFlightSearch
